import { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Trash2,
  Edit3,
  X,
  Check,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Tag,
  ChevronRight,
  FileText,
  ShieldCheck,
  Eye,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import StandardHeader from './layout/StandardHeader';
import MetricCard from './MetricCard';
import { Vision360 } from './common/Vision360';
import { useNavigation } from '../context/NavigationContext';
import { mapLabelToStatusEnum, mapStatusToLabel } from '../utils/statusLabels';
import { apiClient } from '../api/client';
import LoadingState from './ui/LoadingState';
import ErrorState from './ui/ErrorState';
import EmptyState from './ui/EmptyState';

export default function ClientesView({ setCurrentView }: { setCurrentView?: (view: any) => void }) {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [companyFilter, setCompanyFilter] = useState(globalFilters.companyId ? globalFilters.companyId.toString() : 'ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState<'cadastro' | 'visao_360'>('cadastro');
  const [editingClient, setEditingClient] = useState<any>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyId: '',
    status: 'Ativo',
    role: '',
    notes: '',
    tagsInput: '',
  });

  useEffect(() => {
    if (globalFilters.companyId) {
      setCompanyFilter(globalFilters.companyId.toString());
    } else {
      setCompanyFilter('ALL');
    }
  }, [globalFilters.companyId]);

  const { data: customers, isLoading, error, refetch } = useQuery({
    queryKey: ['customers'],
    queryFn: () => apiClient.get('/customers').then((r) => r.data),
  });

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiClient.get('/companies').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/customers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.put(`/customers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      refetch();
    },
  });

  // The API returns an array, but keep the view resilient to envelope responses.
  const clients = Array.isArray(customers)
    ? customers
    : Array.isArray((customers as any)?.data)
      ? (customers as any).data
      : [];
  const companiesList = Array.isArray(companies)
    ? companies
    : Array.isArray((companies as any)?.data)
      ? (companies as any).data
      : [];

  useEffect(() => {
    if (globalFilters.clientId && clients.length > 0) {
      const p = clients.find((proj: any) => proj.id.toString() === globalFilters.clientId.toString());
      if (p) {
        setEditingClient(p);
        setIsModalOpen(true);
        setActiveModalTab('visao_360');
      }
    }
  }, [globalFilters.clientId, clients]);

  const triggerToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleNewClient = () => {
    setEditingClient(null);
    if (globalFilters.clientId) setGlobalFilters({ ...globalFilters, clientId: undefined });
    setActiveModalTab('cadastro');
    setFormData({ name: '', email: '', phone: '', companyId: '', status: 'Ativo', role: '', notes: '', tagsInput: '' });
    setIsModalOpen(true);
  };

  const handleOpenClientWorkspace = (client: any) => {
    setGlobalFilters((previous: any) => ({ ...previous, clientId: client.id }));
    setCurrentView?.('cliente-workspace');
  };

  const handleEditClient = (client: any) => {
    setEditingClient(client);
    setGlobalFilters({ ...globalFilters, clientId: client.id });
    setActiveModalTab('cadastro');
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      companyId: client.companyId ? String(client.companyId) : '',
      status: client.status || 'Ativo',
      role: client.role || '',
      notes: client.notes || '',
      tagsInput: Array.isArray(client.tags) ? client.tags.join(', ') : '',
    });
    setIsModalOpen(true);
  };

  const handleDeleteClient = async (id: string) => {
    if (!confirm('Deseja realmente excluir este cliente permanentemente?')) return;
    try {
      await deleteMutation.mutateAsync(id);
      triggerToast('success', 'Cliente excluído com sucesso!');
    } catch {
      triggerToast('error', 'Falha ao excluir cliente.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      triggerToast('error', 'O nome do cliente é obrigatório.');
      return;
    }

    const tags = formData.tagsInput ? formData.tagsInput.split(',').map((t) => t.trim()).filter((t) => t.length > 0) : [];
    const selectedCompany = companiesList.find((comp: any) => String(comp.id) === String(formData.companyId));
    const companyName = selectedCompany ? selectedCompany.name : '';

    try {
      if (editingClient) {
        const payload = { name: formData.name, industry: formData.role || undefined, status: mapLabelToStatusEnum(formData.status) };
        const updated = await updateMutation.mutateAsync({ id: editingClient.id, data: payload });
        triggerToast('success', 'Cliente atualizado com sucesso!');
      } else {
        const payload = { name: formData.name, industry: formData.role || undefined, status: mapLabelToStatusEnum(formData.status) };
        const created = await createMutation.mutateAsync(payload);
        triggerToast('success', 'Novo cliente cadastrado com sucesso!');
        const createdClient = (created as any)?.data?.data || (created as any)?.data || created;
        if (createdClient?.id) {
          setGlobalFilters((previous: any) => ({ ...previous, clientId: createdClient.id }));
          setCurrentView?.('cliente-workspace');
        }
      }
      setIsModalOpen(false);
    } catch {
      triggerToast('error', 'Falha ao salvar cliente.');
    }
  };

  const filteredClients = clients.filter((c: any) => {
    const matchesSearch =
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
    const matchesCompany = companyFilter === 'ALL' || String(c.companyId) === companyFilter;
    return matchesSearch && matchesStatus && matchesCompany;
  });

  const totalClientsCount = clients.length;
  const activeClientsCount = clients.filter((c) => (c.status || '').toString().toUpperCase() === 'ATIVO' || (c.status || '').toString().toUpperCase() === 'ACTIVE' || (c.status || '').toString().toUpperCase() === '').length;
  const leadsCount = clients.filter((c) => (c.status || '').toString().toUpperCase() === 'LEAD').length;
  const inactiveClientsCount = clients.filter((c) => (c.status || '').toString().toUpperCase() === 'INATIVO' || (c.status || '').toString().toUpperCase() === 'INACTIVE').length;

  if (isLoading) {
    return <LoadingState message="Carregando clientes..." />;
  }

  if (error) {
    return <ErrorState message="Erro ao carregar clientes" retry={() => refetch()} />;
  }

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-10 animate-in fade-in duration-500 relative px-4 sm:px-6 lg:px-10">
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl border text-sm font-bold ${
              toast.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-rose-50 border-rose-100 text-rose-800'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle2 className="text-emerald-500" size={18} /> : <AlertCircle className="text-rose-500" size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <StandardHeader
        title="Gestão de Clientes"
        subtitle="Gerencie contatos, leads e os principais tomadores de decisão das contas corporativas."
        actions={[
          {
            label: 'Novo Cliente',
            icon: UserPlus,
            onClick: handleNewClient,
            variant: 'primary',
          },
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard title="Total de Clientes" value={totalClientsCount} icon={Users} color="text-[#0F172A]" bg="bg-slate-100/80" subtext="Base cadastrada no workspace" secondaryLabel="Contas ativas" secondaryValue={`${activeClientsCount} de ${totalClientsCount}`} progress={totalClientsCount > 0 ? Math.round((activeClientsCount / totalClientsCount) * 100) : 100} progressColor="bg-[#0F172A]" />
        <MetricCard title="Clientes Ativos" value={activeClientsCount} trend="+5%" trendUp={true} icon={CheckCircle2} color="text-[#0F172A]" bg="bg-slate-100/80" subtext="Contas em operação contínua" secondaryLabel="Taxa de Retenção" secondaryValue="85%" progress={85} progressColor="bg-emerald-600" />
        <MetricCard title="Novos Leads" value={leadsCount} trend="+12 este mês" trendUp={true} icon={Sparkles} color="text-[#0F172A]" bg="bg-slate-100/80" subtext="Oportunidades em qualificação" secondaryLabel="Meta de Captação" secondaryValue="60%" progress={60} progressColor="bg-[#0F172A]" />
        <MetricCard title="Atendimento" value="98%" trend="Excelente" trendUp={true} icon={Phone} color="text-[#0F172A]" bg="bg-slate-100/80" subtext="Satisfação NPS e SLA < 2h" secondaryLabel="SLA de Resposta" secondaryValue="98%" progress={98} progressColor="bg-[#0F172A]" />
        <MetricCard title="NPS & Retenção" value="94" trend="+3 pts" trendUp={true} icon={ShieldCheck} color="text-[#0F172A]" bg="bg-slate-100/80" subtext="Índice de Lealdade do Cliente" secondaryLabel="Satisfação Geral" secondaryValue="94%" progress={94} progressColor="bg-[#0F172A]" />
      </div>

      <div className="bg-[#F8FAFC]/40 backdrop-blur-md border border-white rounded-[40px] shadow-[0_20px_60px_rgba(0,0,0,0.05)] p-8 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/10 rounded-full blur-3xl pointer-events-none group-hover:bg-indigo-50/20 transition-colors duration-700" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input type="text" placeholder="Buscar por nome, email ou cargo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/50" />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Status:</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs font-bold text-[#111111] bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-xl focus:outline-none focus:border-indigo-500">
                <option value="ALL">Todos</option>
                <option value="Ativo">Ativo</option>
                <option value="Lead">Lead</option>
                <option value="Inativo">Inativo</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Empresa:</span>
              <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="text-xs font-bold text-[#111111] bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-xl focus:outline-none focus:border-indigo-500 max-w-[200px]">
                <option value="ALL">Todas as Empresas</option>
                {companiesList.map((comp: any) => (
                  <option key={comp.id} value={String(comp.id)}>
                    {comp.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filteredClients.length === 0 ? (
          <div className="py-16 text-center flex flex-col items-center justify-center gap-4 border border-dashed border-slate-100 rounded-2xl bg-slate-50/20">
            <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
              <Users size={24} />
            </div>
            <div className="max-w-xs">
              <h3 className="text-sm font-bold text-[#111111]">Nenhum cliente encontrado</h3>
              <p className="text-xs text-[#64748B] mt-1 leading-relaxed">Não localizamos nenhum cliente que corresponda aos filtros aplicados. Crie um novo cliente para começar.</p>
            </div>
            <button onClick={handleNewClient} className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold px-3.5 py-2 rounded-xl transition-colors cursor-pointer">
              Novo Cliente
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-bold text-[#64748B] uppercase tracking-wider">
                  <th className="pb-3.5 font-bold">Cliente / Cargo</th>
                  <th className="pb-3.5 font-bold">Empresa</th>
                  <th className="pb-3.5 font-bold">Contato</th>
                  <th className="pb-3.5 font-bold">Status</th>
                  <th className="pb-3.5 font-bold">Tags</th>
                  <th className="pb-3.5 font-bold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/50">
                {filteredClients.map((client: any) => {
                  const initial = client.name ? client.name.charAt(0).toUpperCase() : 'C';
                  const _label = mapStatusToLabel(client.status);
                  return (
                    <tr key={client.id} className="hover:bg-slate-50/40 group transition-colors cursor-pointer" onClick={() => handleOpenClientWorkspace(client)}>
                      <td className="py-4 pr-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center text-xs group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">{initial}</div>
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-[#111111] group-hover:text-indigo-600 transition-colors">{client.name}</span>
                            <span className="text-[10px] text-[#64748B] font-medium mt-0.5 flex items-center gap-1">
                              <Briefcase size={10} />
                              {client.role || 'Não especificado'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 pr-3">
                        <div className="flex items-center gap-1.5">
                          <Building2 size={12} className="text-slate-400" />
                          <span className="text-xs font-bold text-[#111111]">{client.companyName || 'Avulso / Sem Empresa'}</span>
                        </div>
                      </td>
                      <td className="py-4 pr-3">
                        <div className="flex flex-col gap-1">
                          {client.email && (
                            <div className="flex items-center gap-1.5 text-[10px] font-medium text-[#64748B] hover:text-indigo-600">
                              <Mail size={10} />
                              <span>{client.email}</span>
                            </div>
                          )}
                          {client.phone && (
                            <div className="flex items-center gap-1.5 text-[10px] font-medium text-[#64748B]">
                              <Phone size={10} />
                              <span>{client.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 pr-3">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                            _label === 'Ativo' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : _label === 'Lead' ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-slate-50 text-slate-500 border border-slate-100'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${_label === 'Ativo' ? 'bg-emerald-500' : _label === 'Lead' ? 'bg-blue-500' : 'bg-slate-400'}`} />
                          {_label}
                        </span>
                      </td>
                      <td className="py-4 pr-3">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {Array.isArray(client.tags) && client.tags.map((tag: string, idx: number) => (
                            <span key={idx} className="text-[9px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
                              <Tag size={8} />
                              {tag}
                            </span>
                          ))}
                          {(!Array.isArray(client.tags) || client.tags.length === 0) && <span className="text-[10px] text-slate-300 font-medium italic">Nenhuma</span>}
                        </div>
                      </td>
                      <td className="py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleOpenClientWorkspace(client)} className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-1 font-bold text-[10px]" title="Abrir Workspace">
                            <Eye size={12} strokeWidth={2.5} />
                            <span>Abrir Workspace</span>
                          </button>
                          <button onClick={() => handleEditClient(client)} className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title="Editar Cadastro">
                            <Edit3 size={12} strokeWidth={2.5} />
                          </button>
                          <button onClick={() => handleDeleteClient(client.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Excluir">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="fixed inset-0 bg-[#111111]/30 backdrop-blur-xs" />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              className={`bg-white border border-[#0F172A0F] rounded-[24px] shadow-2xl z-10 overflow-hidden flex flex-col max-h-[90vh] transition-all duration-300 ${editingClient && activeModalTab === 'visao_360' ? 'max-w-4xl w-full' : 'max-w-xl w-full'}`}
            >
              <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    {editingClient ? <Edit3 size={14} strokeWidth={2.5} /> : <UserPlus size={14} strokeWidth={2.5} />}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#111111]">{editingClient ? 'Cadastro de Cliente' : 'Novo Cadastro de Cliente'}</h3>
                    <p className="text-[10px] font-medium text-[#64748B] mt-0.5">{editingClient ? 'Gerencie as informações e visualize o módulo 360' : 'Registre um novo tomador de decisão no sistema'}</p>
                  </div>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>

              {editingClient && (
                <div className="flex px-6 border-b border-[#0F172A0F] bg-[#FAFAFA]/50 gap-6">
                  <button onClick={() => setActiveModalTab('cadastro')} className={`py-3 px-1 border-b-2 text-xs font-bold transition-all ${activeModalTab === 'cadastro' ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#64748B] hover:text-[#111111]'}`}>
                    Cadastro do Cliente
                  </button>
                  <button onClick={() => setActiveModalTab('visao_360')} className={`py-3 px-1 border-b-2 text-xs font-bold transition-all ${activeModalTab === 'visao_360' ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#64748B] hover:text-[#111111]'}`}>
                    Visão 360°
                  </button>
                </div>
              )}

              {editingClient && activeModalTab === 'visao_360' ? (
                <div className="h-[60vh] overflow-y-auto">
                  <Vision360 entityType="client" entityId={editingClient.id} entityName={editingClient.name} entityData={editingClient} />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 flex-1 overflow-y-auto flex flex-col gap-5 text-left">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Nome Completo *</label>
                    <input type="text" required placeholder="Ex: João da Silva" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">E-mail Corporativo</label>
                      <input type="email" placeholder="joao@empresa.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Telefone / WhatsApp</label>
                      <input type="text" placeholder="(11) 99999-9999" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Cargo / Função</label>
                      <input type="text" placeholder="Ex: Diretor de Tecnologia, CTO, Comprador" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Status Comercial</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 font-bold">
                        <option value="Ativo">Ativo</option>
                        <option value="Lead">Lead / Prospect</option>
                        <option value="Inativo">Inativo</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Empresa Relacionada</label>
                    <select value={formData.companyId} onChange={(e) => setFormData({ ...formData, companyId: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40">
                      <option value="">Nenhuma empresa (Cliente Individual / Avulso)</option>
                          {companiesList.map((comp: any) => (
                        <option key={comp.id} value={String(comp.id)}>
                          {comp.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Tags (separadas por vírgula)</label>
                    <input type="text" placeholder="Ex: tomador-decisao, vip, tech-startup" value={formData.tagsInput} onChange={(e) => setFormData({ ...formData, tagsInput: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Anotações Internas</label>
                    <textarea rows={3} placeholder="Adicione notas sobre o cliente, preferências, histórico de reuniões ou observações comerciais..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full px-3.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors bg-slate-50/40 resize-none" />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#0F172A05]">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer">
                      Cancelar
                    </button>
                    <button type="submit" className="px-5 py-2 text-xs font-bold text-white bg-[#111111] hover:bg-[#222222] rounded-xl shadow-sm hover:scale-[1.01] transition-all cursor-pointer flex items-center gap-1.5">
                      <Check size={14} strokeWidth={2.5} />
                      <span>Salvar Cliente</span>
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
